/**
 * Salesforce config & filter schema — the single source of truth for the field
 * names and enum values the UI needs, so operators don't have to go spelunking
 * through the ps-integrations source every time.
 *
 * Everything here mirrors the backend:
 *   - config writable fields  → SalesforceConfigView.WRITABLE_CREDENTIAL_FIELDS
 *   - filter operators        → salesforce/models/enums.py::FilterOperators
 *   - source objects          → salesforce/models/enums.py::SalesforceObjectType
 *   - importable resources    → configs/enums.py::ImportableResources
 *   - resource configuration  → core/model/subscriber_config.py::SalesforceResourceConfiguration
 *   - common filter fields    → salesforce/models/enums.py::SalesforceFilterFields
 *
 * When the backend enums change, update this file — the forms read from it.
 */

// ---------------------------------------------------------------------------
// Filter operators (FilterOperators)
// ---------------------------------------------------------------------------

/** How the operator consumes `filter_value`. Drives the value input + coercion. */
export type FilterValueMode = "single" | "list" | "none";

export interface FilterOperatorDef {
  /** Wire value the backend expects (the SOQL-ish operator token). */
  value: string;
  label: string;
  help: string;
  valueMode: FilterValueMode;
}

export const FILTER_OPERATORS: FilterOperatorDef[] = [
  {
    value: "is",
    label: "is",
    help: "Field equals the value.",
    valueMode: "single",
  },
  {
    value: "is not",
    label: "is not",
    help: "Field does not equal the value.",
    valueMode: "single",
  },
  {
    value: "known",
    label: "known (not null)",
    help: "Field has any value. No value needed.",
    valueMode: "none",
  },
  {
    value: "empty",
    label: "empty (null)",
    help: "Field is null/blank. No value needed.",
    valueMode: "none",
  },
  {
    value: "in",
    label: "in (any of)",
    help: "Field matches any value in the list. Add one value per row.",
    valueMode: "list",
  },
  {
    value: "not in",
    label: "not in (none of)",
    help: "Field matches none of the values in the list.",
    valueMode: "list",
  },
  {
    value: "less than",
    label: "less than",
    help: "Field < value. Use an ISO date (e.g. 2025-01-01T00:00:00Z) for date fields.",
    valueMode: "single",
  },
  {
    value: "greater than",
    label: "greater than",
    help: "Field > value. Use an ISO date for date fields.",
    valueMode: "single",
  },
];

export function filterOperatorDef(
  value: string,
): FilterOperatorDef | undefined {
  return FILTER_OPERATORS.find((o) => o.value === value);
}

// ---------------------------------------------------------------------------
// Source objects (SalesforceObjectType)
// ---------------------------------------------------------------------------

export interface SourceObjectDef {
  value: string;
  label: string;
  /** The preview/save count query only tallies Contact & Account today. */
  counted: boolean;
}

export const SOURCE_OBJECTS: SourceObjectDef[] = [
  { value: "Account", label: "Account", counted: true },
  { value: "Contact", label: "Contact", counted: true },
  { value: "Opportunity", label: "Opportunity", counted: false },
  { value: "Event", label: "Event", counted: false },
  { value: "Task", label: "Task", counted: false },
  { value: "Activity", label: "Activity", counted: false },
  { value: "RecordType", label: "RecordType", counted: false },
  { value: "Campaign", label: "Campaign", counted: false },
  { value: "User", label: "User", counted: false },
];

/** Objects whose filters actually affect the returned counts. */
export const COUNTED_OBJECTS = SOURCE_OBJECTS.filter((o) => o.counted).map(
  (o) => o.value,
);

// ---------------------------------------------------------------------------
// Common filter fields (SalesforceFilterFields) — suggestions, not a closed set
// ---------------------------------------------------------------------------

export interface FilterFieldSuggestion {
  value: string;
  help: string;
}

export const COMMON_FILTER_FIELDS: FilterFieldSuggestion[] = [
  { value: "instilId__c", help: "Instil's external id on the SF record." },
  { value: "Id", help: "Salesforce record id (Accounts, Opportunities)." },
  { value: "IsClosed", help: "Task is closed (boolean)." },
  { value: "ActivityDateTime", help: "Task activity timestamp (datetime)." },
];

// ---------------------------------------------------------------------------
// Importable resources (ImportableResources) — for resources_to_import
// ---------------------------------------------------------------------------

export interface ResourceDef {
  value: string;
  label: string;
  help?: string;
}

export const IMPORTABLE_RESOURCES: ResourceDef[] = [
  { value: "constituents", label: "Constituents" },
  { value: "contributions", label: "Contributions" },
  { value: "opportunities", label: "Opportunities" },
  { value: "interactions", label: "Interactions" },
  { value: "campaigns", label: "Campaigns" },
  { value: "social_links", label: "Social links" },
  { value: "users", label: "Users" },
  { value: "custom_fields", label: "Custom fields" },
];

/**
 * Exportable resources (SalesforceExportableResourceTypes) — the closed set the
 * backend accepts for resources_to_export / resources_to_delete. Only these
 * three have an export path; anything else is rejected with a 400.
 * Constituent assignees live under resource_configuration, not this list.
 */
export const EXPORTABLE_RESOURCES: ResourceDef[] = [
  {
    value: "constituents",
    label: "Constituents",
    help: "Contact + Account. The only value the delete pass honours.",
  },
  {
    value: "contributions",
    label: "Contributions",
    help: "Opportunity + Payment.",
  },
  { value: "interactions", label: "Interactions", help: "Activity." },
];

// ---------------------------------------------------------------------------
// Resource configuration (SalesforceCredentials.resource_configuration)
// Assignees and related flags are per-resource, not top-level resource lists.
// ---------------------------------------------------------------------------

export const CONSTITUENTS_RESOURCE_NAME = "constituents";
export const INTERACTIONS_RESOURCE_NAME = "interactions";
export const ASSIGNEES_SUBRESOURCE = "assignees";

/** Resources that may appear in resource_configuration. */
export const CONFIGURABLE_RESOURCES: ResourceDef[] = [
  { value: CONSTITUENTS_RESOURCE_NAME, label: "Constituents" },
  { value: INTERACTIONS_RESOURCE_NAME, label: "Interactions" },
];

/**
 * Subresources allowed under constituents.
 * Each enabled entry is stored as `{ subresource, dry_run }` (not a bare string).
 */
export const CONSTITUENT_SUBRESOURCES: ResourceDef[] = [
  {
    value: ASSIGNEES_SUBRESOURCE,
    label: "Assignees",
    help: "Constituent assignee import/export. Replaces the old top-level constituent_assignees resource. Each entry carries its own dry_run flag.",
  },
];

/**
 * Subresources allowed under interactions.
 * Same object shape as constituents: `{ subresource, dry_run }`.
 */
export const INTERACTION_SUBRESOURCES: ResourceDef[] = [
  {
    value: ASSIGNEES_SUBRESOURCE,
    label: "Assignees",
    help: "Interaction assignee import/export. Each entry carries its own dry_run flag.",
  },
];

/** Per-subresource dry_run — shown when a subresource is enabled. */
export const SUBRESOURCE_DRY_RUN: ResourceDef = {
  value: "dry_run",
  label: "Dry run",
  help: "When on, prepare assignee sync without writing. Defaults to on when enabling assignees (matches bare-string coercion on the API).",
};

/** Flags allowed under constituents. */
export const CONSTITUENT_RESOURCE_FLAGS: ResourceDef[] = [
  {
    value: "clear_out_empty_assignees_on_import",
    label: "Clear empty assignees on import",
    help: "When importing, clear assignees that are empty in Salesforce.",
  },
  {
    value: "clear_out_empty_assignees_on_export",
    label: "Clear empty assignees on export",
    help: "When exporting, clear assignees that are empty in Instil.",
  },
];

export interface ResourceConfigurationSectionDef {
  resourceName: string;
  label: string;
  help: string;
  subresources: ResourceDef[];
  flags: ResourceDef[];
  /** Show Subresources to export when the backend accepts that list. */
  supportsExport: boolean;
}

export const RESOURCE_CONFIGURATION_SECTIONS: ResourceConfigurationSectionDef[] =
  [
    {
      resourceName: CONSTITUENTS_RESOURCE_NAME,
      label: "Resource configuration — constituents",
      help: `Assignees are a subresource of constituents, not a top-level import/export resource. Each enabled entry is { "subresource": "${ASSIGNEES_SUBRESOURCE}", "dry_run": … } under subresources_to_import / subresources_to_export.`,
      subresources: CONSTITUENT_SUBRESOURCES,
      flags: CONSTITUENT_RESOURCE_FLAGS,
      supportsExport: true,
    },
    {
      resourceName: INTERACTIONS_RESOURCE_NAME,
      label: "Resource configuration — interactions",
      help: `Assignees are a subresource of interactions, not a top-level import/export resource. Each enabled entry is { "subresource": "${ASSIGNEES_SUBRESOURCE}", "dry_run": … } under subresources_to_import / subresources_to_export.`,
      subresources: INTERACTION_SUBRESOURCES,
      flags: [],
      supportsExport: true,
    },
  ];

// ---------------------------------------------------------------------------
// Batch export (POST …/salesforce/<id>/export/)
// ---------------------------------------------------------------------------

/**
 * Resources accepted by SalesforceBatchExportView / StartBatchExportToSalesforce.
 * Today only constituent assignees have a batch-export path.
 */
export const BATCH_EXPORTABLE_RESOURCES: ResourceDef[] = [
  {
    value: "constituent_assignees",
    label: "Constituent assignees",
    help: "Push Instil constituent assignees to Salesforce Account/Contact lookup fields.",
  },
];

export const CONSTITUENT_ASSIGNEES_EXPORT_RESOURCE = "constituent_assignees";

// ---------------------------------------------------------------------------
// Config fields (SalesforceConfigView writable set) — drives ConfigEditor docs
// ---------------------------------------------------------------------------

export type ConfigFieldKind = "text" | "secret" | "bool" | "int" | "enum-multi";

export interface ConfigFieldDef {
  key: string;
  label: string;
  kind: ConfigFieldKind;
  help: string;
  placeholder?: string;
  /** For enum-multi. */
  options?: ResourceDef[];
}

/** The connection triple — required, rendered as its own block. */
export const CONNECTION_FIELDS: ConfigFieldDef[] = [
  {
    key: "auth_url",
    label: "Auth / Instance URL",
    kind: "text",
    help: "Salesforce login/auth URL (My Domain or sandbox). The instance URL in the header is resolved from this.",
    placeholder: "https://your-org.my.salesforce.com",
  },
  {
    key: "client_id",
    label: "Client ID (consumer key)",
    kind: "text",
    help: "Connected-app consumer key.",
  },
  {
    key: "client_secret",
    label: "Client Secret (consumer secret)",
    kind: "secret",
    help: "Connected-app consumer secret. Write-only — the API never returns it. Leave blank to keep the stored one.",
  },
];

/** Everything else the PUT accepts — preserved on partial save (read-modify-write). */
export const SETTINGS_FIELDS: ConfigFieldDef[] = [
  {
    key: "export_to_sf_enabled",
    label: "Export to Salesforce enabled",
    kind: "bool",
    help: "Whether Instil pushes records back out to Salesforce.",
  },
  {
    key: "resources_to_import",
    label: "Resources to import",
    kind: "enum-multi",
    help: "Which resource types to pull from Salesforce into Instil. Assignees belong under resource configuration, not here.",
    options: IMPORTABLE_RESOURCES,
  },
  {
    key: "resources_to_export",
    label: "Resources to export",
    kind: "enum-multi",
    help: "Which resource types Instil pushes back out to Salesforce. Assignees belong under resource configuration, not here.",
    options: EXPORTABLE_RESOURCES,
  },
  {
    key: "resources_to_delete",
    label: "Resources to delete",
    kind: "enum-multi",
    help: "Resource types eligible for deletion on import. Only constituents is acted on today.",
    options: EXPORTABLE_RESOURCES,
  },
  {
    key: "import_contact_social_links",
    label: "Import contact social links",
    kind: "bool",
    help: "Pull social links from Contact records.",
  },
  {
    key: "import_affiliation_social_links",
    label: "Import affiliation social links",
    kind: "bool",
    help: "Pull social links from affiliation records.",
  },
  {
    key: "import_relationship_social_links",
    label: "Import relationship social links",
    kind: "bool",
    help: "Pull social links from relationship records.",
  },
  {
    key: "import_interval_in_minutes",
    label: "Import interval (minutes)",
    kind: "int",
    help: "How often the recurring import runs, in minutes.",
    placeholder: "e.g. 1440",
  },
];
